/**
 * Reading URL search params safely.
 *
 * TanStack Router JSON-parses search values, so `?q=449` arrives as the
 * **number** 449 and `?id=16` as the number 16 — not strings. A guard written
 * as `typeof s.q === 'string' ? s.q : ''` therefore throws those away, and the
 * failure is silent and inverted: the list renders *unfiltered*, so a dropped
 * search looks like a successful one that matched everything.
 *
 * It bites exactly the values an operator is most likely to paste or scan —
 * barcodes, PLUs, phone numbers, account numbers, record ids — and it does not
 * show up in normal use, because the app's own links round-trip through the
 * router's own serializer.
 *
 * Use these helpers in every `validateSearch`. Coerce, don't discard.
 */

/** A required text param. Numbers become their decimal string; anything else
 *  that cannot sensibly be text becomes the fallback. */
export function asText(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'bigint') return String(value);
  return fallback;
}

/** An optional text param: empty and absent both collapse to undefined, so a
 *  `?id=` left behind by a cleared selection does not read as a selection. */
export function asOptionalText(value: unknown): string | undefined {
  const text = asText(value);
  return text === '' ? undefined : text;
}

/** A param constrained to a known set — anything else falls back. */
export function asEnum<const T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  const text = asText(value);
  return (allowed as readonly string[]).includes(text) ? (text as T) : fallback;
}

/** A positive integer page number. `?page=0` and `?page=abc` both mean page 1. */
export function asPage(value: unknown, fallback = 1): number {
  const n = typeof value === 'number' ? value : Number(asText(value));
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

/**
 * An integer param (cents, ids, limits). Returns undefined when absent or
 * unparseable, so a caller can tell "not set" from "set to zero".
 *
 * The empty check is load-bearing: `Number('')` is `0`, not `NaN`, so a naive
 * `Number.isFinite` test turns an absent param into a real zero. That shipped
 * briefly and made an applied card-number filter also apply `amount = $0.00`,
 * which matched nothing.
 */
export function asInt(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number')
    return Number.isFinite(value) ? Math.trunc(value) : undefined;
  const text = asText(value).trim();
  if (text === '') return undefined;
  const n = Number(text);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}
